import {
  EDataTableType,
  foreignKeysForTable,
  MaxPageRecordsRetainInState,
} from '../../data/Constants';
import { PaginatedData, IPaginatedResponse, IPaginatedState } from '../../../types/IComponentTypes';

/**
 * Updates maintenance row data in a paginated data structure with memory management.
 *
 * ⚠️ ONLY USE INSIDE REDUX TOOLKIT REDUCERS - This function mutates the data object in place,
 * which is only safe within Redux Toolkit's Immer-powered reducers.
 *
 * This utility function manages paginated data storage by:
 * - Updating existing records based on a search key
 * - Enforcing a maximum number of pages to retain in state (only when adding NEW pages)
 * - Preserving memory by removing older pages when the limit is exceeded
 *
 * @template T - The type of items in the data array. Must have optional 'code' and 'id' properties.
 * @param {PaginatedData<T>} data - Object containing arrays of items keyed by page number (mutated in place)
 * @param {T[]} responseData - Array of new/updated items from the API response (first item used)
 * @param {number} page - Current page number being processed
 * @param {string} searchKey - The code value used to identify which existing item to update
 * @returns {void} Mutates the data object in place
 *
 * @example
 * // Inside a Redux Toolkit reducer:
 * const data = { 1: [{ code: 'A1', id: '123' }] };
 * const newData = [{ code: 'A1', name: 'Updated' }];
 * updateMaintenanceRowData(data, newData, 1, 'A1');
 * // Result: { 1: [{ code: 'A1', id: '123', name: 'Updated' }] } - newData merged with existing id
 */
export function updateMaintenanceRowData<T extends { code?: string; id?: string | number }>(
  data: PaginatedData<T>,
  responseData: T[],
  page: number,
  searchKey: string,
  type?: string
): void {
  // Early return if no response data
  if (!responseData || responseData.length === 0) {
    return;
  }

  // Check if this is a NEW page being added
  const isNewPage = !(page in data);

  // Memory management: Only remove oldest page when adding a NEW page that exceeds the limit
  if (isNewPage && Object.keys(data).length >= MaxPageRecordsRetainInState) {
    const pageNumbers = Object.keys(data).map(Number);
    const oldestPage = Math.min(...pageNumbers);

    // Ensure we don't delete the page we're about to add
    if (oldestPage !== page) {
      delete data[oldestPage];
    }
  }

  // Get or initialize page data
  if (!data[page]) {
    data[page] = [];
  }

  // Find and update existing item that matches searchKey with fresh API data
  const keyField = type ? foreignKeysForTable[type as EDataTableType] : 'code';
  const matchIndex = data[page].findIndex(
    (item) => item && item[keyField as keyof T] === searchKey
  );

  if (matchIndex !== -1) {
    // Preserve existing id, merge with new data
    data[page][matchIndex] = {
      ...responseData[0],
      id: data[page][matchIndex].id,
    };
  }
}

/**
 * Updates maintenance data in the application state with pagination support and memory management.
 * Mutates the state object in place (safe within Redux Toolkit/Immer context).
 *
 * This utility function manages state updates for paginated maintenance screens by:
 * - Resetting data when navigating back to page 1 (handles recordsPerPage changes)
 * - Enforcing memory limits by removing old pages when the limit is exceeded
 * - Updating pagination metadata (page, total count, records per page)
 * - Setting search key from the action payload
 *
 * @param state - The Redux state slice (mutated in place)
 * @param action - The action payload containing page data and metadata
 *
 * @example
 * const state = { data: {}, searchKey: '', currentPage: 1, totalCount: 0, recordsPerPage: 15 };
 * const action = { page: 1, data: [...], totalRecords: 100, recordsPerPage: 15 };
 * updateTableData(state, action);
 */
export function updateTableData<T = any>(
  state: IPaginatedState<T>,
  action: IPaginatedResponse<T>
): void {
  state.currentPage = action.page;
  state.totalCount = action.totalRecords;
  state.recordsPerPage = action.recordsPerPage ? action.recordsPerPage : 15;
  state.searchKey = action.searchKey ?? '';

  if (action.page === 1) {
    state.data = {}; // Reset data on every load of first page
  }

  // Memory management: Limit the number of cached pages
  const dataKeys = Object.keys(state.data ?? {});
  if (
    dataKeys.length >= MaxPageRecordsRetainInState &&
    action.page !== 1 &&
    !dataKeys.includes(String(action.page))
  ) {
    delete state.data[Math.min(...dataKeys.map(Number))];
  }

  // Generate synthetic IDs only if data items don't already have one
  const hasOwnId = action.data?.length > 0 && 'id' in (action.data[0] as Record<string, unknown>);
  if (!hasOwnId) {
    action.data = action.data
      ? action.data?.map((item, index) => ({
          ...item,
          id: action.page + '-' + (index + 1),
        }))
      : [];
  }

  state.data = {
    ...state.data,
    [action.page]: action.data ? action.data : [],
  };
}
